/**
 * `/admin/:insee/engagements`: Engagements (`docs/ADMIN.md` §2) — who asked
 * for what, and how to hand that to the tools that use it. Three blocks:
 * the counts per engagement kind (already on the dashboard payload, no
 * extra endpoint needed), the buildings still missing a referent
 * (`docs/ADMIN.md` §3.3 — the gap a `building_referent` engagement is
 * meant to fill, so it belongs here as much as on the Commune screen), and
 * the CSV export of §3.6.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { exportEngagements, getBuildingReferents, getCommuneDashboard } from '../../../api/admin';
import { ApiError } from '../../../api/client';
import { Button, Card, Checkbox, Select, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { ENGAGEMENT_KINDS } from '../../household/components/EngagementList';
import { ENGAGEMENT_LABEL_KEYS } from '../../household/labels';

import type { ApiErrorBody, CommuneDashboard, EngagementKind } from '../../../api/types';
import type { BuildingReferents } from '../types';

interface PageData {
  dashboard: CommuneDashboard;
  referents: BuildingReferents;
}

/** The export's filename, `docs/ADMIN.md` §3.6: `dring-<insee>-<kind>-<YYYY-MM-DD>.csv`. */
function exportFilename(insee: string, kind: EngagementKind): string {
  const date = new Date().toISOString().slice(0, 10);
  return `dring-${insee}-${kind}-${date}.csv`;
}

/**
 * Read a `Blob`’s content as text via `FileReader` rather than
 * `Blob.prototype.text()`: the latter is unimplemented on jsdom’s `Blob`
 * (confirmed against this project’s own test suite), while `FileReader` —
 * the older, still universally supported way to read one — works both
 * there and in every real browser this console targets.
 *
 * @param blob - The blob to read.
 * @returns The blob's content, decoded as text.
 */
function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsText(blob);
  });
}

/**
 * Count the data rows of a CSV export's bytes: the BOM-prefixed, `\r\n`-
 * joined text of `docs/ADMIN.md` §3.6, minus its header row. The response
 * carries no count of its own, so this is how the confirmation toast and
 * the "nothing to export" case are told apart.
 *
 * @param blob - The export's response body.
 * @returns How many data rows the CSV holds (0 when only the header is present).
 */
async function countCsvRows(blob: Blob): Promise<number> {
  const text = (await readBlobAsText(blob)).replace(/^\uFEFF/, '');
  const lines = text.split('\r\n').filter((line) => line.length > 0);
  return Math.max(lines.length - 1, 0);
}

/** Resolve the French message for a caught export API error. */
function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    const body = cause.body as ApiErrorBody | undefined;
    if (body?.detail) return body.detail;
  }
  return t('admin.engagements.exportError');
}

/**
 * Render the Engagements screen.
 *
 * @returns The page element.
 */
export function EngagementsPage() {
  const { insee = '' } = useParams<{ insee: string }>();
  const { showToast } = useToast();
  const [data, setData] = useState<PageData | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  const [kind, setKind] = useState<EngagementKind>(ENGAGEMENT_KINDS[0]);
  const [includeExported, setIncludeExported] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setLoadError(false);
    Promise.all([getCommuneDashboard(insee), getBuildingReferents(insee)])
      .then(([dashboard, referents]) => {
        if (!cancelled) setData({ dashboard, referents });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [insee]);

  /** Export the selected kind, download it as a file, and report how many rows it held. */
  async function handleExport() {
    setExporting(true);
    try {
      const blob = await exportEngagements(insee, { kind, include_exported: includeExported });
      const count = await countCsvRows(blob);
      if (count === 0) {
        showToast(t('admin.engagements.exportEmpty'), 'info');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename(insee, kind);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast(t('admin.engagements.exportSuccess', { count }), 'success');
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setExporting(false);
    }
  }

  if (loadError) {
    return (
      <p role="alert" className="admin-error">
        {t('admin.engagements.loadError')}
      </p>
    );
  }

  if (!data) {
    return (
      <div className="admin-page">
        <Skeleton height="4rem" />
        <Skeleton height="10rem" />
      </div>
    );
  }

  const { dashboard, referents } = data;
  const missingReferents = referents.rows.filter((row) => !row.has_referent);

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <h1>{t('admin.engagements.title')}</h1>
        <p className="admin-lead">{t('admin.engagements.lead')}</p>
      </div>

      <Card className="admin-block">
        <p className="admin-kicker">{t('admin.engagements.countsKicker')}</p>
        <div className="admin-kpis">
          {ENGAGEMENT_KINDS.map((engagementKind) => (
            <div className="admin-kpi" key={engagementKind}>
              <p className="admin-kpi__value">
                {t(
                  dashboard.engagements[engagementKind] > 1
                    ? 'admin.engagements.countLabelPlural'
                    : 'admin.engagements.countLabel',
                  { count: dashboard.engagements[engagementKind] },
                )}
              </p>
              <p className="admin-kpi__label">{t(ENGAGEMENT_LABEL_KEYS[engagementKind])}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="admin-block">
        <p className="admin-kicker">{t('admin.engagements.referentsKicker')}</p>
        <p className="admin-lead">
          {t(
            missingReferents.length > 1
              ? 'admin.engagements.referentsLeadPlural'
              : 'admin.engagements.referentsLead',
            { missing: missingReferents.length, total: referents.buildings },
          )}
        </p>
        {missingReferents.length === 0 ? (
          <p className="admin-muted">{t('admin.engagements.referentsEmpty')}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.commune.referents.building')}</th>
                  <th scope="col">{t('admin.commune.referents.doors')}</th>
                  <th scope="col">{t('admin.commune.referents.households')}</th>
                </tr>
              </thead>
              <tbody>
                {missingReferents.map((row) => (
                  <tr key={row.address_id}>
                    <td>{row.label}</td>
                    <td>{row.doors}</td>
                    <td>{row.households}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="admin-block">
        <p className="admin-kicker">{t('admin.engagements.exportKicker')}</p>
        <p className="admin-lead">{t('admin.engagements.exportLead')}</p>
        <div className="admin-export-form">
          <Select
            label={t('admin.engagements.kindSelectLabel')}
            value={kind}
            onChange={(event) => setKind(event.target.value as EngagementKind)}
            options={ENGAGEMENT_KINDS.map((engagementKind) => ({
              value: engagementKind,
              label: t(ENGAGEMENT_LABEL_KEYS[engagementKind]),
            }))}
          />
          <Checkbox
            label={t('admin.engagements.includeExportedLabel')}
            checked={includeExported}
            onChange={(event) => setIncludeExported(event.target.checked)}
          />
          <Button onClick={() => void handleExport()} disabled={exporting}>
            {exporting
              ? t('admin.engagements.exportButtonPending')
              : t('admin.engagements.exportButton')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
